import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const PerformanceSettings = () => {
  const [cachingEnabled, setCachingEnabled] = useState(true);
  const [imageOptimization, setImageOptimization] = useState(true);
  const [lazyLoading, setLazyLoading] = useState(true);
  const [minification, setMinification] = useState(true);

  const performanceMetrics = [
    {
      label: 'Page Load Time',
      value: '1.2s',
      status: 'good',
      benchmark: '< 2s',
      icon: 'Zap'
    },
    {
      label: 'First Contentful Paint',
      value: '0.8s',
      status: 'good',
      benchmark: '< 1s',
      icon: 'Eye'
    },
    {
      label: 'Time to Interactive',
      value: '2.1s',
      status: 'warning',
      benchmark: '< 2s',
      icon: 'MousePointer'
    },
    {
      label: 'Total Page Size',
      value: '1.8 MB',
      status: 'good',
      benchmark: '< 2 MB',
      icon: 'HardDrive'
    }
  ];

  const optimizationRecommendations = [
    {
      title: 'Enable Image Compression',
      description: 'Reduce image file sizes by up to 70% without visible quality loss',
      impact: 'High',
      status: 'completed'
    },
    {
      title: 'Implement Code Splitting',
      description: 'Load JavaScript bundles on-demand to reduce initial load time',
      impact: 'High',
      status: 'pending'
    },
    {
      title: 'Use CDN for Static Assets',
      description: 'Serve images and files from geographically distributed servers',
      impact: 'Medium',
      status: 'pending'
    },
    {
      title: 'Enable Browser Caching',
      description: 'Store frequently accessed resources in user browsers',
      impact: 'Medium',
      status: 'completed'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'good':
        return 'text-success';
      case 'warning':
        return 'text-warning';
      case 'error':
        return 'text-destructive';
      default:
        return 'text-muted-foreground';
    }
  };

  const getImpactColor = (impact) => {
    switch (impact) {
      case 'High':
        return 'bg-destructive/10 text-destructive';
      case 'Medium':
        return 'bg-warning/10 text-warning';
      case 'Low':
        return 'bg-success/10 text-success';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <h3 className="font-heading font-semibold text-lg md:text-xl text-foreground mb-4 md:mb-6">
          Performance Metrics
        </h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          {performanceMetrics?.map((metric, index) => (
            <div key={index} className="p-4 bg-background border border-border rounded-lg">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Icon name={metric?.icon} size={20} className="text-primary" />
                  <span className="caption font-medium text-foreground">{metric?.label}</span>
                </div>
                <Icon
                  name={metric?.status === 'good' ? 'CheckCircle' : 'AlertCircle'}
                  size={20}
                  className={getStatusColor(metric?.status)}
                />
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-heading font-bold text-foreground">{metric?.value}</span>
                <span className="text-xs text-muted-foreground">/ {metric?.benchmark}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between p-4 bg-success/10 border border-success/20 rounded-lg">
          <div className="flex items-center gap-3">
            <Icon name="TrendingUp" size={24} className="text-success" />
            <div>
              <p className="caption font-medium text-success">Overall Performance Score</p>
              <p className="text-xs text-muted-foreground mt-1">Your site is performing well</p>
            </div>
          </div>
          <span className="text-3xl font-heading font-bold text-success">87/100</span>
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <h3 className="font-heading font-semibold text-lg md:text-xl text-foreground mb-4 md:mb-6">
          Optimization Settings
        </h3>
        
        <div className="space-y-4">
          <div className="p-4 bg-background border border-border rounded-lg">
            <Checkbox
              label="Enable Caching"
              description="Store frequently accessed data to reduce server load and improve response times"
              checked={cachingEnabled}
              onChange={(e) => setCachingEnabled(e?.target?.checked)}
            />
            {cachingEnabled && (
              <div className="mt-3 ml-6 p-3 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground mb-2">Cache Duration:</p>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value="24"
                    className="w-20 px-2 py-1 bg-background border border-border rounded text-sm text-foreground"
                    readOnly
                  />
                  <span className="text-sm text-foreground">hours</span>
                </div>
              </div>
            )}
          </div>

          <div className="p-4 bg-background border border-border rounded-lg">
            <Checkbox
              label="Image Optimization"
              description="Automatically compress and resize images for optimal loading performance"
              checked={imageOptimization}
              onChange={(e) => setImageOptimization(e?.target?.checked)}
            />
            {imageOptimization && (
              <div className="mt-3 ml-6 space-y-2">
                <Checkbox
                  label="Convert to WebP format"
                  checked
                  onChange={() => {}}
                  size="sm"
                />
                <Checkbox
                  label="Generate responsive images"
                  checked
                  onChange={() => {}}
                  size="sm"
                />
              </div>
            )}
          </div>

          <div className="p-4 bg-background border border-border rounded-lg">
            <Checkbox
              label="Lazy Loading"
              description="Load images and content only when they enter the viewport"
              checked={lazyLoading}
              onChange={(e) => setLazyLoading(e?.target?.checked)}
            />
          </div>

          <div className="p-4 bg-background border border-border rounded-lg">
            <Checkbox
              label="Code Minification"
              description="Remove unnecessary characters from code to reduce file sizes"
              checked={minification}
              onChange={(e) => setMinification(e?.target?.checked)}
            />
          </div>
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h3 className="font-heading font-semibold text-lg md:text-xl text-foreground">
              Optimization Recommendations
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Suggested improvements to enhance performance
            </p>
          </div>
          <Button variant="outline" size="sm" iconName="RefreshCw" iconPosition="left">
            Refresh
          </Button>
        </div>

        <div className="space-y-3">
          {optimizationRecommendations?.map((recommendation, index) => (
            <div
              key={index}
              className="p-4 bg-background border border-border rounded-lg hover:shadow-sm transition-all duration-250"
            >
              <div className="flex flex-col sm:flex-row items-start justify-between gap-3 mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="caption font-semibold text-foreground">{recommendation?.title}</h4>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getImpactColor(recommendation?.impact)}`}>
                      {recommendation?.impact} Impact
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">{recommendation?.description}</p>
                </div>
                {recommendation?.status === 'completed' ? (
                  <div className="flex items-center gap-1 text-success">
                    <Icon name="CheckCircle" size={16} />
                    <span className="text-xs font-medium">Completed</span>
                  </div>
                ) : (
                  <Button variant="default" size="sm">
                    Apply
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <h3 className="font-heading font-semibold text-lg md:text-xl text-foreground mb-4 md:mb-6">
          Performance History
        </h3>
        
        <div className="space-y-4">
          <div className="h-48 bg-muted rounded-lg flex items-center justify-center">
            <div className="text-center">
              <Icon name="BarChart3" size={48} className="text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Performance chart visualization</p>
              <p className="text-xs text-muted-foreground mt-1">Last 30 days</p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="p-3 bg-background border border-border rounded-lg text-center">
              <p className="text-xs text-muted-foreground mb-1">Avg Load Time</p>
              <p className="text-lg font-heading font-bold text-foreground">1.3s</p>
            </div>
            <div className="p-3 bg-background border border-border rounded-lg text-center">
              <p className="text-xs text-muted-foreground mb-1">Uptime</p>
              <p className="text-lg font-heading font-bold text-success">99.9%</p>
            </div>
            <div className="p-3 bg-background border border-border rounded-lg text-center">
              <p className="text-xs text-muted-foreground mb-1">Total Requests</p>
              <p className="text-lg font-heading font-bold text-foreground">45.2K</p>
            </div>
            <div className="p-3 bg-background border border-border rounded-lg text-center">
              <p className="text-xs text-muted-foreground mb-1">Bandwidth</p>
              <p className="text-lg font-heading font-bold text-foreground">12.4 GB</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceSettings;